import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { showConfirm } from './ConfirmDialog';
import {
  apiListLayoutPresets,
  apiGetLayoutPreset,
  apiCreateLayoutPreset,
  apiUpdateLayoutPreset,
  apiDeleteLayoutPreset,
  type LayoutPresetKind,
  type LayoutPresetScope,
  type LayoutPresetSummary,
} from '../api';
import { useWriteGate } from '../lib/writeGate';
import { createDefaultPdfLayout } from '../lib/riderLayout';
import { createDefaultDaysheetLayout } from '../lib/daysheetLayout';
import { createDefaultTourBookLayout } from '../lib/tourbookLayout';

/**
 * BUILT-IN "Default (factory)" preset (2026-11-03).
 *
 * The factory layout is NOT stored anywhere: it is RECOMPUTED on every apply from the same pure
 * factories the generators use (`createDefault*Layout()`), so it can never drift from the code —
 * unlike a saved preset, which is a snapshot taken on a given day.
 *
 * Why it is not gated by `layout.presets`: applying the built-in performs NO server call on the
 * presets API (so there is no `canWriteScope` on the server side to mirror). The only thing that
 * matters is that the layout being edited is writable — the editor's own autosave then persists it
 * under the same server guard as any manual edit. Gating it on `layout.presets` would forbid a
 * member who legitimately holds `layout.daysheet` from resetting the layout they are allowed to edit.
 */
const BUILTIN_FACTORY: Record<LayoutPresetKind, () => any> = {
  rider: createDefaultPdfLayout,
  daysheet: createDefaultDaysheetLayout,
  tourbook: createDefaultTourBookLayout,
};

/**
 * Shared "Presets" panel for the three layout editors (Rider / Daysheet /
 * TourBook).
 *
 * UNIFIED SCOPE (v0.559): the panel lists EVERY preset the user can reach —
 * the global ones AND the band ones of all their bands — whatever the scope
 * currently being edited ("Global template" / "This band"). Each row carries a
 * scope BADGE (Global / the band name) and its own Apply button, so a band
 * preset can be loaded into the global template and vice versa.
 *
 * Applying only loads the preset into the editor's WORKING layout
 * (`onApply(data)`); the editor's existing autosave then persists it to the
 * scope being edited — which is the target, and whose write right is checked by
 * the server (`canWriteScope`). The panel mirrors that right from the list
 * payload (`canWriteGlobal` / `canWriteBands`) so a refused apply shows an
 * explicit message instead of failing silently.
 */
interface Props {
  kind: LayoutPresetKind;
  scope: LayoutPresetScope;
  bandId: string | null;
  layout: any;
  onApply: (data: any) => void;
}

export default function LayoutPresetsPanel({ kind, scope, bandId, layout, onApply }: Props) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2) — CAPABILITY, never a blanket `canWrite`:
  // `/api/layout-presets` is a DELEGATED family mounted BEFORE the server's global
  // read-only guard (server/index.js l.415), decided by `layout.presets` alone.
  // Freezing the whole panel on `canWrite` would REGRESS the verified matrix
  // feature for a read-only account that legitimately holds `layout.presets`.
  // A normal owner/editor is unaffected (`canWriteBand` short-circuits to true).
  const writeGate = useWriteGate(bandId);
  const canManagePresets = writeGate.canWriteCap('layout.presets');
  const [presets, setPresets] = useState<LayoutPresetSummary[]>([]);
  const [canWriteGlobal, setCanWriteGlobal] = useState(false);
  const [canWriteBands, setCanWriteBands] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiListLayoutPresets(kind);
      setPresets(res.presets);
      setCanWriteGlobal(res.canWriteGlobal);
      setCanWriteBands(res.canWriteBands);
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.loadFailed'));
    }
  }, [kind, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ---- Target scope: the layout currently being edited -------------------
  // Its write right decides whether an apply may happen at all.
  const canWriteTarget =
    scope === 'global' ? canWriteGlobal : !!bandId && canWriteBands.includes(bandId);

  const targetLabel =
    scope === 'global' ? t('layoutPresets.scopeGlobal') : t('layoutPresets.scopeBand');

  // Clear, explicit reason shown when the current target cannot be written —
  // never a silent failure.
  const targetDenied = !canWriteTarget
    ? scope === 'global'
      ? t('layoutPresets.globalWriteDenied')
      : !bandId
        ? t('layoutPresets.noBandTarget')
        : t('layoutPresets.bandWriteDenied')
    : null;

  const presetBadge = (p: LayoutPresetSummary) =>
    p.scope === 'global'
      ? t('layoutPresets.scopeGlobal')
      : p.band_name || t('layoutPresets.scopeBand');

  const handleApply = async (p: LayoutPresetSummary) => {
    // Delegated capability first, then the server mirror (canWriteScope).
    if (!canManagePresets) return;
    if (!canWriteTarget) {
      setNotice(null);
      setError(targetDenied);
      return;
    }
    setBusyId(p.id);
    setError(null);
    setNotice(null);
    try {
      const detail = await apiGetLayoutPreset(p.id);
      onApply(detail.data);
      setNotice(t('layoutPresets.applied', { name: p.name, target: targetLabel }));
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.applyFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleApplyBuiltin = () => {
    // No presets API involved ⇒ the write right that matters is the TARGET layout's.
    if (!canWriteTarget) {
      setNotice(null);
      setError(targetDenied);
      return;
    }
    setError(null);
    onApply(BUILTIN_FACTORY[kind]());
    setNotice(t('layoutPresets.applied', { name: t('layoutPresets.builtinName'), target: targetLabel }));
  };

  const handleSaveAs = async () => {
    if (!canManagePresets) return; // `layout.presets` not granted (server 403s)
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await apiCreateLayoutPreset({ kind, scope, bandId: scope === 'band' ? bandId : null, name, data: layout });
      setNewName('');
      await load();
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const startRename = (p: LayoutPresetSummary) => {
    setRenamingId(p.id);
    setRenameValue(p.name);
    setError(null);
    setNotice(null);
  };

  const handleRename = async () => {
    if (!canManagePresets) return; // `layout.presets` not granted (server 403s)
    if (renamingId === null) return;
    const name = renameValue.trim();
    if (!name) return;
    setBusyId(renamingId);
    setError(null);
    try {
      await apiUpdateLayoutPreset(renamingId, { name });
      setRenamingId(null);
      setRenameValue('');
      await load();
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.renameFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (p: LayoutPresetSummary) => {
    if (!canManagePresets) return; // before the confirm dialog AND the API call
    if (!(await showConfirm(t('layoutPresets.deleteConfirm')))) return;
    setBusyId(p.id);
    setError(null);
    setNotice(null);
    try {
      await apiDeleteLayoutPreset(p.id);
      await load();
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  // Overwrite THIS preset's stored layout with the layout currently being
  // edited (distinct from Apply, which goes the other way: preset -> editor).
  // Guarded like Apply: the write targets the preset (p.can_write) but saving
  // is only meaningful / allowed when the current layout itself is editable
  // in its scope (canWriteTarget) — mirror the server canWriteScope guard.
  const handleSaveToPreset = async (p: LayoutPresetSummary) => {
    if (!canManagePresets) return; // before the confirm dialog AND the API call
    if (!canWriteTarget) {
      setNotice(null);
      setError(targetDenied);
      return;
    }
    if (!(await showConfirm(t('layoutPresets.saveToConfirm', { name: p.name })))) return;
    setBusyId(p.id);
    setError(null);
    setNotice(null);
    try {
      await apiUpdateLayoutPreset(p.id, { data: layout });
      setNotice(t('layoutPresets.saved', { name: p.name }));
      await load();
    } catch (err: any) {
      setError(err?.message || t('layoutPresets.saveToFailed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="form-section layout-presets-panel">
      <h2>{t('layoutPresets.title')}</h2>

      {/* Built-in factory default — ALWAYS first, never stored, recomputed from the code. */}
      <ul className="layout-preset-list">
        <li className="layout-preset-row" data-scope="builtin" data-testid="preset-builtin-default">
          <span className="layout-preset-name">{t('layoutPresets.builtinName')}</span>
          <span className="preview-scope-badge layout-preset-badge" data-testid="preset-builtin-badge">
            {t('layoutPresets.builtinBadge')}
          </span>
          <span className="layout-preset-actions">
            <button
              type="button"
              className="btn btn-primary btn-small"
              onClick={handleApplyBuiltin}
              disabled={!canWriteTarget}
              title={targetDenied || t('layoutPresets.builtinHint')}
              data-testid="preset-builtin-apply"
            >
              {t('layoutPresets.apply')}
            </button>
          </span>
        </li>
      </ul>

      {/* All saved presets, whatever the scope being edited. */}
      <ul className="layout-preset-list">
        {presets.map((p) => (
          <li key={p.id} className="layout-preset-row" data-scope={p.scope} data-preset-id={p.id}>
            {renamingId === p.id ? (
              <>
                <input
                  className="form-input layout-preset-rename"
                  value={renameValue}
                  aria-label={t('layoutPresets.rename')}
                  disabled={!canManagePresets}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void handleRename(); }
                    if (e.key === 'Escape') { setRenamingId(null); }
                  }}
                />
                <button type="button" className="btn btn-primary btn-small" onClick={handleRename} disabled={!renameValue.trim() || busyId === p.id || !canManagePresets}>
                  {t('layoutPresets.renameSave')}
                </button>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => setRenamingId(null)}>
                  {t('layoutPresets.cancel')}
                </button>
              </>
            ) : (
              <>
                <span className="layout-preset-name">{p.name}</span>
                <span
                  className={`preview-scope-badge layout-preset-badge${p.scope === 'global' ? '' : ' is-band'}`}
                  data-testid="preset-scope-badge"
                >
                  {presetBadge(p)}
                </span>
                <span className="layout-preset-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-small"
                    onClick={() => handleApply(p)}
                    disabled={!canWriteTarget || busyId === p.id || !canManagePresets}
                    title={targetDenied || t('layoutPresets.applyTo', { target: targetLabel })}
                  >
                    {t('layoutPresets.apply')}
                  </button>
                  {p.can_write && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => startRename(p)}
                      disabled={busyId === p.id || !canManagePresets}
                    >
                      {t('layoutPresets.rename')}
                    </button>
                  )}
                  {p.can_write && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => handleSaveToPreset(p)}
                      disabled={!canWriteTarget || busyId === p.id || !canManagePresets}
                      title={targetDenied || t('layoutPresets.saveTo')}
                      data-testid="preset-save-current"
                    >
                      {t('layoutPresets.saveTo')}
                    </button>
                  )}
                  {p.can_write && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => handleDelete(p)}
                      disabled={busyId === p.id || !canManagePresets}
                    >
                      {t('layoutPresets.delete')}
                    </button>
                  )}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
      {!presets.length && <p className="empty-hint">{t('layoutPresets.empty')}</p>}

      <div className="form-row" style={{ alignItems: 'center', gap: 8 }}>
        <div className="form-group" style={{ flex: 1 }}>
          <input
            className="form-input"
            value={newName}
            placeholder={t('layoutPresets.namePlaceholder')}
            disabled={!canManagePresets}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleSaveAs(); } }}
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleSaveAs}
          disabled={!newName.trim() || busy || !canManagePresets}
        >
          {t('layoutPresets.saveAs')}
        </button>
      </div>

      <p className="text-dim" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
        {t('layoutPresets.hint')}
      </p>
      {targetDenied && (
        <p className="save-status-err layout-presets-denied" data-testid="presets-target-denied">{targetDenied}</p>
      )}
      {notice && <p className="save-status-ok layout-presets-notice">{notice}</p>}
      {error && <p className="save-status-err">{error}</p>}
    </div>
  );
}
